import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogueHandler, AccountingSystemDialogueHandler } from './types';
import AccountingSystemContext from './AccountingSystemContext';
import styles from '../AccountingSystemOpen.module.scss';

const { ConfirmationDialogue, Box } = vp;

type AccountingSystemDialogueProps = {
  handleDialogue: DialogueHandler<AccountingSystemDialogueHandler>;
};

type FC = FunctionComponent<AccountingSystemDialogueProps>;

const AccountingSystemDialogue: FC = ({ handleDialogue }: AccountingSystemDialogueProps) => {
  const {
    showInsufficientFunds,
    showError,
    errorMessage,
    showCancel,
    type,
    showNoPaymentMethod,
    showCancelSchedule,
    showScheduleAll,
    showScheduleCustomDate,
    hasItemsWithNoPaymentMethod,
  } = useContext(AccountingSystemContext);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab.Dialogs' });
  return useMemo(
    () => (
      <div>
        {/* Sending Error Confirmation */}
        <ConfirmationDialogue
          isVisible={showError}
          title={t('Error.title', { defaultValue: 'Error! The transaction was not completed.' })}
          status="error"
          subTitle={`${errorMessage}`}
          confirm={{
            label: t('Error.back', { defaultValue: 'Back' }),
            action: () => handleDialogue({ type: 'error', state: 'confirm' }),
          }}
        />
        {/* Insufficient fund Confirmation */}
        <ConfirmationDialogue
          isVisible={showInsufficientFunds}
          title={t('InsufficientFunds.title', { defaultValue: 'Insufficient Funds' })}
          status="error"
          subTitle={t('InsufficientFunds.subTitle', {
            defaultValue: 'Your VoPay Balance does not have enough funds to complete this action.',
          })}
          confirm={{
            label: t('InsufficientFunds.fundButton', { defaultValue: 'Fund Vopay Account' }),
            action: () => handleDialogue({ type: 'insufficient', state: 'confirm' }),
          }}
          cancel={{
            label: t('InsufficientFunds.maybeLater', { defaultValue: 'Maybe Later' }),
            action: () => handleDialogue({ type: 'insufficient', state: 'fail' }),
          }}
        />
        {/* Cancel Bulk Confirmation */}
        <ConfirmationDialogue
          isVisible={showCancel}
          title={
            type === 'Receivable'
              ? t('Cancel.title_Receivable', { defaultValue: 'Cancel Collection' })
              : t('Cancel.title_Payable', { defaultValue: 'Cancel Payment' })
          }
          status="alert"
          subTitle={t('Cancel.subTitle', { defaultValue: 'All new account details added will be discarded.' })}
          buttonWidth="auto"
          confirm={{
            label:
              type === 'Receivable'
                ? t('Cancel.confirm_Receivable', { defaultValue: 'Cancel Collection' })
                : t('Cancel.confirm_Payable', { defaultValue: 'Cancel Payment' }),
            action: () => handleDialogue({ type: 'cancel', state: 'confirm' }),
          }}
          cancel={{
            label: t('Cancel.goBack', { defaultValue: 'Go Back' }),
            action: () => handleDialogue({ type: 'cancel', state: 'fail' }),
          }}
        />
        {/* No Payment Method Confirmation */}
        <ConfirmationDialogue
          isVisible={showNoPaymentMethod}
          title={t('NoPaymentMethod.title', { defaultValue: 'No Payment Method' })}
          status="error"
          subTitle={t('NoPaymentMethod.subTitle', {
            defaultValue: 'Payable cannot be scheduled without a payment method',
          })}
          confirm={{
            label: t('NoPaymentMethod.addButton', { defaultValue: 'Add Payment Method' }),
            action: () => handleDialogue({ type: 'noPaymentMethod', state: 'confirm' }),
          }}
        />
        {/* Cancel Schedule Confirmation */}
        <ConfirmationDialogue
          isVisible={showCancelSchedule}
          title={
            type === 'Receivable'
              ? t('CancelSchedule.title_Receivable', { defaultValue: 'Cancel Scheduled Collection' })
              : t('CancelSchedule.title_Payable', { defaultValue: 'Cancel Scheduled Payment' })
          }
          status="alert"
          subTitle={t('CancelSchedule.subTitle', { defaultValue: 'Are you sure you want to cancel?' })}
          buttonWidth="auto"
          confirm={{
            label: t('CancelSchedule.yesCancel', { defaultValue: 'Yes, Cancel' }),
            action: () => handleDialogue({ type: 'cancelSchedule', state: 'confirm' }),
          }}
          cancel={{
            label: t('CancelSchedule.keepScheduled', { defaultValue: 'Keep Scheduled' }),
            action: () => handleDialogue({ type: 'cancelSchedule', state: 'fail' }),
          }}
        />
        {/* Schedule All Payments */}
        <ConfirmationDialogue
          isVisible={showScheduleAll}
          width="600px"
          title={
            type === 'Receivable'
              ? t('ScheduleAll.title_Receivable', { defaultValue: 'Schedule Collection for All' })
              : t('ScheduleAll.title_Payable', { defaultValue: 'Schedule Payment for All' })
          }
          status="alert"
          subTitle={
            <Box base="8" gap="md" flx="col">
              <div>
                {type === 'Receivable'
                  ? t('ScheduleAll.subTitle_Receivable', {
                      defaultValue:
                        'This will Schedule collection for all on their respective due dates. Please confirm if you wish to proceed.',
                    })
                  : t('ScheduleAll.subTitle_Payable', {
                      defaultValue:
                        'This will Schedule payment for all on their respective due dates. Please confirm if you wish to proceed.',
                    })}
              </div>
              {hasItemsWithNoPaymentMethod && (
                <div className={styles.dialogueTerm}>
                  {type === 'Receivable'
                    ? t('ScheduleAll.noPaymentMethod_Receivable', {
                        defaultValue: 'Receivables without Payment Methods will not be scheduled for collection.',
                      })
                    : t('ScheduleAll.noPaymentMethod_Payable', {
                        defaultValue: 'Payables without Payment Methods will not be scheduled for payment.',
                      })}
                </div>
              )}
            </Box>
          }
          buttonWidth="auto"
          confirm={{
            label: t('ScheduleAll.confirm', { defaultValue: 'Confirm' }),
            action: () => handleDialogue({ type: 'scheduleAll', state: 'confirm' }),
          }}
          cancel={{
            label: t('ScheduleAll.cancel', { defaultValue: 'Cancel' }),
            action: () => handleDialogue({ type: 'scheduleAll', state: 'fail' }),
          }}
        />
        {/* Schedule All Customdate */}
        <ConfirmationDialogue
          isVisible={showScheduleCustomDate}
          width="600px"
          title={
            type === 'Payable'
              ? t('ScheduleCustomDate.title_Payable', { defaultValue: 'Schedule selected Payables?' })
              : t('ScheduleCustomDate.title_Receivable', { defaultValue: 'Schedule selected Receivables?' })
          }
          status="alert"
          subTitle={
            <Box base="8" gap="md" flx="col">
              <div>
                {type === 'Payable'
                  ? t('ScheduleCustomDate.subTitle_Payable', {
                      defaultValue:
                        'Some Payables you selected will be scheduled for the custom date instead of their original due date.',
                    })
                  : t('ScheduleCustomDate.subTitle_Receivable', {
                      defaultValue:
                        'Some Receivables you selected will be scheduled for the custom date instead of their original due date.',
                    })}
              </div>
            </Box>
          }
          buttonWidth="auto"
          confirm={{
            label: t('ScheduleCustomDate.confirm', { defaultValue: 'Confirm' }),
            action: () => handleDialogue({ type: 'scheduleCustomDate', state: 'confirm' }),
          }}
          cancel={{
            label: t('ScheduleCustomDate.cancel', { defaultValue: 'Cancel' }),
            action: () => handleDialogue({ type: 'scheduleCustomDate', state: 'fail' }),
          }}
        />
      </div>
    ),
    [
      showInsufficientFunds,
      showError,
      errorMessage,
      showCancel,
      type,
      showNoPaymentMethod,
      showCancelSchedule,
      showScheduleAll,
      showScheduleCustomDate,
      hasItemsWithNoPaymentMethod,
    ],
  );
};

export default AccountingSystemDialogue;
